import { Formatters } from '../shared/lib/formatters';
import { LoadingSpinner } from '../shared/components';

// Internal Expenses — the per-property "fees sheet" (what each owner pays for
// and what we charge). R100 A2 (Jesse): promoted out of the Admin page's
// expenses tab into a standalone page so the Accounting hub can mount it for
// the external accounting partner (who never sees Admin). Reads/writes
// /api/internal-expenses, which is the SAME 'sr_rates' store the owner-
// statement reviewer checks against. Admin's tab now mounts this class too —
// one implementation.
export class InternalExpensesPage {
  private container: HTMLElement;
  private expenseRows: any[] = [];
  private customColumns: { key: string; label: string }[] = [];
  private expenseFormulas: any[] = [];
  private expenseSearch = '';
  private dirty = false;   // review R100 #14: unsaved grid edits are DOM-only — the 15-min refresh must not eat them
  // R114 fix (data-safety #0): the sheet is a full-replace editor. A failed
  // GET must never draw an editable (blank) grid, and every Save carries the
  // version of the sheet it was drawn from — the server refuses (409) a stale
  // or blank base instead of writing it over every property's rates.
  private loadError = '';
  private version = '';
  // R114 fix2: one sheet write at a time (Save / add column / remove column) —
  // each carries this.version, so a write sent while another is in flight
  // carries a base that is about to go stale.
  private busy = false;

  constructor(container: HTMLElement) { this.container = container; }

  async render(): Promise<void> {
    this.container.innerHTML = `<div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div>`;
    await this.load();
    this.draw();
  }

  private async post(url: string, body: any): Promise<any> {
    return fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      .then(r => r.ok ? r.json() : null).catch(() => null);
  }

  private async load(): Promise<void> {
    const get = (u: string) => fetch(u).then(r => r.ok ? r.json() : null).catch(() => null);
    const sheet = fetch('/api/internal-expenses')
      .then(async r => ({ ok: r.ok, j: await r.json().catch(() => null) }))
      .catch(() => ({ ok: false, j: null as any }));
    const [res, formulas] = await Promise.all([sheet, get('/api/formulas')]);
    const ok = res.ok && Array.isArray(res.j?.rows) && !!res.j?.version;
    this.loadError = ok ? '' : String((res.j && res.j.error) || "Couldn't load the fees sheet just now — nothing was changed. Reload the page in a moment.");
    this.expenseRows = ok ? res.j.rows : [];
    this.customColumns = ok && Array.isArray(res.j.custom_columns) ? res.j.custom_columns : [];
    this.version = ok ? String(res.j.version) : '';
    this.expenseFormulas = Array.isArray(formulas) ? formulas : [];
  }

  private draw(): void {
    const host = this.container;
    const esc = (v: any) => Formatters.escapeHtml(String(v == null ? '' : v));
    if (this.loadError) {
      // No grid and no Save button: a sheet that didn't load must not be editable.
      host.innerHTML = `
        <div class="page-content">
          <div class="page-intro">
            <h1>Internal expenses</h1>
            <p>What each owner pays for and what we charge — the single source the owner-statement reviewer checks against.</p>
          </div>
          <div class="kpi-card" role="alert" style="padding:18px 20px; display:flex; gap:12px; align-items:center; flex-wrap:wrap;">
            <div style="flex:1 1 260px; font-size:13px; color:var(--danger-text);">${esc(this.loadError)}</div>
            <button class="btn btn-primary btn-sm" id="ie-reload" style="min-height:36px;">Reload</button>
          </div>
        </div>`;
      host.querySelector('#ie-reload')?.addEventListener('click', () => { void this.render(); });
      return;
    }
    const seededN = this.expenseRows.filter(r => r.seeded).length;
    // Formula cell = one of the app's REAL revenue formulas (same fee engine as
    // the Properties registry) plus a '— none —' option. An unknown saved code
    // stays selectable so a stale assignment survives a round-trip.
    const codes = this.expenseFormulas.map((f: any) => String(f.formula_code));
    const formulaOpts = (sel: string) => {
      const s = String(sel || '');
      const extra = (s && !codes.includes(s)) ? `<option value="${esc(s)}" selected>${esc(s)}</option>` : '';
      return `<option value="" ${!s ? 'selected' : ''}>— none —</option>` + extra +
        this.expenseFormulas.map((f: any) =>
          `<option value="${esc(f.formula_code)}" ${String(f.formula_code) === s ? 'selected' : ''}>${esc(f.formula_code)}</option>`).join('');
    };
    const ni = (_v: any, w = 56) => // numeric input
      `style="width:${w}px; font-size:11px; padding:3px 5px; border:1px solid var(--border); border-radius:5px; text-align:right;"`;

    const q = this.expenseSearch;
    const rows = this.expenseRows.filter(r => !q || String(r.name || '').toLowerCase().includes(q));

    const rec = (r: any) => r.recurring || {};
    const cust = (r: any) => r.custom || {};
    const body = rows.map(r => {
      const id = esc(r.listing_id);
      const re = rec(r);
      const cu = cust(r);
      const cell = (key: string, w = 56, val?: any) =>
        `<td><input data-ie-field="${key}" data-ie-id="${id}" type="number" step="any" value="${esc(val ?? r[key])}" ${ni(0, w)}></td>`;
      const recCell = (k: string) =>
        `<td><input data-ie-rec="${k}" data-ie-id="${id}" type="number" step="any" value="${esc(re[k])}" ${ni(0, 56)}></td>`;
      const custCell = (k: string) =>
        `<td><input data-ie-cust="${esc(k)}" data-ie-id="${id}" type="text" value="${esc(cu[k])}" style="width:120px; font-size:11px; padding:3px 6px; border:1px solid var(--border); border-radius:5px;"></td>`;
      return `<tr data-ie-row="${id}">
        <td class="ie-prop" title="${esc(r.name)}"><div style="font-weight:600;">${esc(r.name)}</div><div class="mono num" style="color:var(--text-muted); font-size:10px;">${id}${r.seeded ? ' <span class="pill pill-amber">seeded</span>' : ''}</div></td>
        <td><select data-ie-field="formula_code" data-ie-id="${id}">${formulaOpts(r.formula_code || '')}</select></td>
        ${cell('mgmt_pct', 52)}
        ${cell('mgmt_pct_mtr', 52)}
        ${cell('tech', 58)}
        ${cell('damage_waiver', 58)}
        ${cell('cleaning_rate', 62)}
        <td style="text-align:center;"><input data-ie-field="withholding" data-ie-id="${id}" type="checkbox" ${r.withholding ? 'checked' : ''}></td>
        ${recCell('lawn')}${recCell('pool')}${recCell('pest')}${recCell('garbage')}${recCell('wifi')}${recCell('streaming')}
        <td style="text-align:center;"><input data-ie-field="pest_every_other_month" data-ie-id="${id}" type="checkbox" ${r.pest_every_other_month ? 'checked' : ''}></td>
        ${cell('reserves', 62)}
        <td><input data-ie-field="note" data-ie-id="${id}" type="text" value="${esc(r.note)}" style="width:200px; font-size:11px; padding:3px 6px; border:1px solid var(--border); border-radius:5px;"></td>
        ${this.customColumns.map(c => custCell(c.key)).join('')}
      </tr>`;
    }).join('');

    host.innerHTML = `
      <div class="page-content">
      <style>
        .ie-wrap { overflow-x:auto; border:1px solid var(--border); border-radius:var(--radius); background:var(--card); box-shadow:var(--shadow-card); }
        .ie-tbl { border-collapse:collapse; font-size:12.5px; width:max-content; min-width:100%; font-variant-numeric:tabular-nums; }
        .ie-tbl th { position:sticky; top:0; z-index:2; background:var(--card); padding:9px 10px; text-align:left; font-size:10.5px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.06em; border-bottom:1px solid var(--border); white-space:nowrap; }
        .ie-tbl td { padding:7px 10px; border-bottom:1px solid var(--border-soft); white-space:nowrap; }
        .ie-tbl tbody tr { transition:background .1s ease; }
        .ie-tbl tbody tr:hover { background:var(--bg-subtle); }
        .ie-tbl tbody tr:hover td.ie-prop { background:var(--bg-subtle); }
        .ie-tbl tbody tr:last-child td { border-bottom:none; }
        .ie-tbl td.ie-prop { position:sticky; left:0; background:var(--card); z-index:1; min-width:170px; max-width:230px; overflow:hidden; }
        .ie-tbl td.ie-prop > div:first-child { overflow:hidden; text-overflow:ellipsis; }
        .ie-tbl th:first-child { position:sticky; left:0; z-index:3; }
        .ie-tbl select { font-size:11.5px; padding:4px 7px; border:1px solid var(--border); border-radius:6px; background:var(--card); color:var(--text); max-width:130px; }
        .ie-grp { color:var(--text); }
      </style>
      <div class="page-intro">
        <h1>Internal expenses</h1>
        <p>What each owner pays for and what we charge — the single source the owner-statement reviewer checks against.</p>
      </div>
      <div style="display:flex; gap:10px; align-items:center; flex-wrap:wrap; margin-bottom:10px;">
        <span class="mono num" style="font-size:12px; color:var(--text-muted);">${this.expenseRows.length} properties${seededN ? ` · ${seededN} seeded (no data yet)` : ''}</span>
        <input id="ie-search" type="search" placeholder="Search…" value="${esc(this.expenseSearch)}"
               style="font-size:12px; padding:5px 10px; border:1px solid var(--border); border-radius:6px; width:180px;">
        <button class="btn btn-secondary btn-sm" id="ie-add-col">+ Add column</button>
        <button class="btn btn-primary btn-sm" id="ie-save" style="margin-left:auto;">Save internal expenses</button>
        <span id="ie-saved" style="font-size:12px; color:var(--text-muted);"></span>
      </div>
      <div class="ie-wrap">
        <table class="ie-tbl">
          <thead><tr>
            <th>Property</th><th>Formula</th><th>Mgmt %</th><th>MTR %</th>
            <th>Tech $</th><th>Damage waiver $</th><th>Cleaning rate $</th><th>7% WH</th>
            <th class="ie-grp">Lawn $</th><th class="ie-grp">Pool $</th><th class="ie-grp">Pest $</th><th class="ie-grp">Garbage $</th><th class="ie-grp">WiFi $</th><th class="ie-grp">Streaming $</th>
            <th>Pest EOM</th><th>Reserves $</th><th>Note</th>
            ${this.customColumns.map(c => `<th>${esc(c.label)} <a data-ie-del-col="${esc(c.key)}" title="Remove column" style="color:var(--text-muted); cursor:pointer;">×</a></th>`).join('')}
          </tr></thead>
          <tbody>${body || `<tr><td colspan="${17 + this.customColumns.length}" style="padding:20px; text-align:center; color:var(--text-muted);">No properties match.</td></tr>`}</tbody>
        </table>
      </div>
      </div>`;
    this.bind();
  }

  private bind(): void {
    const host = this.container;
    const $ = <T extends HTMLElement>(sel: string) => host.querySelector<T>(sel);
    host.querySelectorAll<HTMLElement>('[data-ie-field], [data-ie-rec], [data-ie-cust]').forEach(el => el.addEventListener('input', () => { this.dirty = true; }));
    host.querySelectorAll<HTMLElement>('[data-ie-field], [data-ie-rec], [data-ie-cust]').forEach(el => el.addEventListener('change', () => { this.dirty = true; }));
    $<HTMLInputElement>('#ie-search')?.addEventListener('input', (e) => {
      this.expenseSearch = (e.target as HTMLInputElement).value.toLowerCase();
      this.draw();
      const inp = $<HTMLInputElement>('#ie-search');
      if (inp) { inp.focus(); inp.setSelectionRange(inp.value.length, inp.value.length); }
    });
    $('#ie-save')?.addEventListener('click', async () => {
      if (this.busy) return;
      const btn = $<HTMLButtonElement>('#ie-save');
      const saved = $('#ie-saved');
      this.busy = true;
      if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }
      const rows = this.readRows();
      this.expenseRows = rows; // keep in-memory rows in sync with what we just saved
      try {
        const res = await fetch('/api/internal-expenses', {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ rows, base_version: this.version }),
        });
        const j = await res.json().catch(() => ({}));
        // 409 (stale/blank base) / 503 (store unreadable): nothing was saved —
        // the server's text says reload, then redo; the edits stay on screen.
        if (saved) saved.textContent = res.ok ? `Saved ${j.count ?? rows.length} rows` : (j.error || 'Save failed');
        if (res.ok) { this.dirty = false; if (j.version) this.version = String(j.version); }
      } catch (e: any) {
        if (saved) saved.textContent = e?.message || 'Save failed';
      } finally {
        this.busy = false;
        if (btn) { btn.disabled = false; btn.textContent = 'Save internal expenses'; }
      }
    });

    // Formula cell → assign the REAL revenue formula (same fee engine as the
    // Properties registry) AND remember the code on the row so Save persists it
    // for the reviewer's commission check. Picking '— none —' just clears the
    // row's code locally (no assignment is posted).
    host.querySelectorAll<HTMLSelectElement>('[data-ie-field="formula_code"]').forEach(sel =>
      sel.addEventListener('change', () => {
        const id = sel.dataset.ieId!;
        const code = sel.value;
        const row = this.expenseRows.find(r => String(r.listing_id) === id);
        if (row) row.formula_code = code;
        if (code) void this.post('/api/formula-assignments', { listing_id: id, formula_code: code, effective_from: new Date().toISOString().slice(0, 10) });
      }));

    $('#ie-add-col')?.addEventListener('click', async () => {
      if (this.busy) return;
      const label = (window.prompt('New column name') || '').trim();
      if (!label || this.busy) return;
      this.busy = true;
      this.expenseRows = this.readRows(); // don't lose unsaved edits on redraw
      try {
        const res = await fetch('/api/internal-expenses/columns', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'add', label, base_version: this.version }),
        }).then(r => r.ok ? r.json() : null).catch(() => null);
        if (res?.custom_columns) this.customColumns = res.custom_columns;
        if (res?.version) this.version = String(res.version);
      } finally { this.busy = false; }
      this.draw();
    });

    host.querySelectorAll<HTMLElement>('[data-ie-del-col]').forEach(el =>
      el.addEventListener('click', async () => {
        if (this.busy) return;
        const key = el.dataset.ieDelCol!;
        if (!window.confirm('Remove this column?') || this.busy) return;
        this.busy = true;
        this.expenseRows = this.readRows();
        try {
          const res = await fetch('/api/internal-expenses/columns', {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'remove', key, base_version: this.version }),
          }).then(r => r.ok ? r.json() : null).catch(() => null);
          if (res?.custom_columns) this.customColumns = res.custom_columns;
          if (res?.version) this.version = String(res.version);
        } finally { this.busy = false; }
        this.draw();
      }));
  }

  // Read the editable grid out of the DOM into the rows payload. Reads every
  // listing's row (not just the filtered view) so a search filter never drops
  // rows from the save.
  private readRows(): any[] {
    const host = this.container;
    const byId = new Map(this.expenseRows.map(r => [String(r.listing_id), r]));
    host.querySelectorAll<HTMLElement>('[data-ie-row]').forEach(tr => {
      const id = tr.dataset.ieRow!;
      const base = byId.get(id) || { listing_id: id };
      const next: any = { ...base, recurring: { ...(base.recurring || {}) }, custom: { ...(base.custom || {}) } };
      const numOrBlank = (el: HTMLInputElement) => el.value.trim() === '' ? '' : Number(el.value);
      tr.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[data-ie-field]').forEach(el => {
        const f = (el as HTMLElement).dataset.ieField!;
        if ((el as HTMLInputElement).type === 'checkbox') next[f] = (el as HTMLInputElement).checked;
        else if ((el as HTMLInputElement).type === 'number') next[f] = numOrBlank(el as HTMLInputElement);
        else next[f] = (el as HTMLInputElement).value;
      });
      tr.querySelectorAll<HTMLInputElement>('[data-ie-rec]').forEach(el => { next.recurring[el.dataset.ieRec!] = numOrBlank(el); });
      tr.querySelectorAll<HTMLInputElement>('[data-ie-cust]').forEach(el => { next.custom[el.dataset.ieCust!] = el.value; });
      byId.set(id, next);
    });
    return Array.from(byId.values());
  }

  // Draft-safety for App.autoRefresh: an unsaved grid edit must not be wiped.
  suspendAutoRefresh(): boolean {
    if (this.dirty) return true;
    const a = document.activeElement as HTMLElement | null;
    return !!(a && this.container.contains(a) && /^(INPUT|SELECT|TEXTAREA)$/.test(a.tagName));
  }
}
